import React, { useState } from 'react';
import Spinner from '@atlaskit/spinner';
import { useInfiniteQuery } from '@tanstack/react-query';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import Button from '@atlaskit/button';
import BulletListIcon from '@atlaskit/icon/glyph/bullet-list';

import { apiFetch } from '@/services';
import apiMeta, { CRUDableType } from '@/meta';
import { extendWidth, spaceTopComfortable, spaceTopCozy } from '@/styles';
import { useFetchErrorController } from '@/controllers';

import ChangeList from './change-list';
import { mapRevisionsToChangeList, mapChangeListEnhancedRevisionType } from './change-list/utils';
import { testIds } from './constants';

interface RevisionLogsProps {
  entityType: CRUDableType;
  publicId: string;
  withTitle?: boolean;
  lazyload?: boolean;
}

const RevisionLogs: React.FC<RevisionLogsProps> = ({
  entityType,
  publicId,
  withTitle,
  lazyload,
}) => {
  /*
  NOTE:
  The TOME backend uses `size` and `beforeRev` to implement cursor-based pagination.
  `beforeRev` should be set to the oldest revision last received.
  */

  const { onError } = useFetchErrorController();
  const [shouldLoadHistory, setShouldLoadHistory] = useState(false);
  const isHistoryShow = !lazyload || shouldLoadHistory;

  const pageSize = 20;

  const fetchRevisionLogs = async ({ pageParam = 0 }) => {
    const beforeRev = pageParam === 0 ? '' : `&beforeRev=${pageParam}`;
    const { endpoints } = apiMeta[entityType];

    // Check if the entity type has a history endpoint
    if (!('history' in endpoints) || typeof endpoints.history !== 'function') {
      throw new Error(`History endpoint not available for entity type: ${entityType}`);
    }

    const revs = await apiFetch(`${endpoints.history(publicId)}?size=${pageSize}${beforeRev}`);
    return revs;
  };

  const { isInitialLoading: isLoading, hasNextPage, fetchNextPage, data, error } = useInfiniteQuery(
    [entityType, 'revision-logs', publicId],
    fetchRevisionLogs,
    {
      enabled: isHistoryShow,
      onError,
      getNextPageParam: (lastPage, _) => {
        const { content } = lastPage;
        if ((content?.length ?? 0) > 0) {
          const rev: API.RevisionLog = content[content.length - 1];
          const { revision } = rev;
          return revision;
        }
        //  Terminate
        return undefined;
      },
    }
  );

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: !!error,
    rootMargin: '0px 0px 400px 0px',
  });

  const changeList = mapChangeListEnhancedRevisionType(
    (data?.pages ?? []).flatMap(page => mapRevisionsToChangeList(page.content)) || []
  );

  return (
    <>
      <section data-testid="revision-logs" css={[spaceTopComfortable, extendWidth]}>
        {withTitle && <h4>Change History</h4>}
        <div css={withTitle ? spaceTopCozy : ''}>
          {!isHistoryShow && (
            <Button
              iconBefore={<BulletListIcon label="" />}
              onClick={() => {
                setShouldLoadHistory(true);
              }}
              testId={testIds.showChangeLogButton}
            >
              Show Change Log
            </Button>
          )}
          {isHistoryShow && (
            <>
              {isLoading && <Spinner size="medium" />}
              {!isLoading && (
                <>
                  {data && <ChangeList changeList={changeList} />}
                  {hasNextPage && (
                    <div css={spaceTopComfortable}>
                      <Spinner ref={sentryRef} size="medium" />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default React.memo(RevisionLogs);
